import type { Attribute } from "../types";

/**
 * 統一された選択肢オプションの型定義
 */
export interface Option {
  value: string;
  label: string;
  description: string | null | undefined;
}

/**
 * selectionItemsから選択肢を取得
 */
const getSelectionOptions = (attribute: Attribute): Option[] => {
  if (!attribute.selectionItems?.length) {
    return [];
  }

  return attribute.selectionItems.map((item) => ({
    value: item.value,
    label: item.label,
    description: item.dictKey,
  }));
};

/**
 * divisionItemsから選択肢を取得
 */
const getDivisionOptions = (attribute: Attribute): Option[] => {
  if (!attribute.divisionItems?.items?.length) {
    return [];
  }

  // divisionItemsをそのまま使用
  const filteredItems = attribute.divisionItems.items;

  return filteredItems.map((item) => ({
    value: item.id,
    label: item.label,
    description: undefined,
  }));
};

/**
 * 属性定義から選択肢を統一形式で取得する
 */
export const getAttributeOptions = (attribute: Attribute): Option[] => {
  // selectionItemsを優先
  const selectionOptions = getSelectionOptions(attribute);
  if (selectionOptions.length > 0) {
    return selectionOptions;
  }

  return getDivisionOptions(attribute);
};

/**
 * 動的フィールド用の静的選択肢を取得
 */
export const getStaticFieldOptions = (attribute: Attribute): Option[] => {
  return getAttributeOptions(attribute);
};

/**
 * 選択肢配列から特定の値に対応するオプションを検索
 *
 * @param options - 選択肢配列
 * @param value - 検索する値
 * @returns 見つかったオプション、存在しない場合はundefined
 */
export const findOptionByValue = (
  options: Option[],
  value: string,
): Option | undefined => {
  return options.find((option) => option.value === value);
};

/**
 * 複数の値に対応するオプションを検索
 *
 * @param options - 選択肢配列
 * @param values - 検索する値の配列
 * @returns 見つかったオプションの配列
 */
export const findOptionsByValues = (
  options: Option[],
  values: string[],
): Option[] => {
  return values
    .map((value) => findOptionByValue(options, value))
    .filter((option): option is Option => option !== undefined);
};

/**
 * 選択肢が空かどうかを判定
 *
 * @param options - 選択肢配列
 * @returns 空の場合はtrue
 */
export const isOptionsEmpty = (options: Option[]): boolean => {
  return options.length === 0;
};
